import React from 'react';
import PropTypes from 'prop-types';
import ReactDomServer from 'react-dom/server';
import markerWithLabelLoader from 'markerwithlabel';
import { isEqual, forEach, isEmpty } from 'lodash';
import { IMAGE_GOOGLE_MAP } from 'constants/filePaths';

// local dependencies
import { googleMap as googleMapView } from './googleMap.view';
import { messageBus } from 'core/modules/message-bus';
import { GOOGLE_MAP_FOCUS_LOCATION } from 'constants/message-bus-events';

// import { getLabel } from 'utils/getLabel';
// import { withAsyncDependencies } from 'ui/hoc/withAsyncDependencies';

/**
 * @type {function}
 * @desc A Higher-Order-Component to enhance functionality of `Component`
 * @param {object} Component - A React component
 * @example
 * import { googleMap, GoogleMap } from 'modules/googleMap';
 *
 * // create a component
 * const ComponentWithHOC = googleMap( CustomView );
 * @return {class} - A standard React component
 */
const googleMap = ( Component = googleMapView ) => {
    class GoogleMap extends React.Component {
        constructor( props ) {
            log.debug( 'GoogleMap.constructor()' );

            super( props );

            // map element reference
            this.mapElemRef = React.createRef();

            // component static data
            this.data = {
                map: null, // google map instance
                markers: {} // map markers
            };

            // bind events and method
            this.__bind();
        }

        /**
         * @desc Bind events and methods
         */
        __bind() {
            this.renderGoogleMap = this.renderGoogleMap.bind( this );
            this.handleFocusLocation = this.handleFocusLocation.bind( this );

            messageBus.on( `${ GOOGLE_MAP_FOCUS_LOCATION }__${ this.props.id }`, this.handleFocusLocation, this );
        }

        /**
         * @desc Unbind events
         */
        __unbind() {
            messageBus.off( `${ GOOGLE_MAP_FOCUS_LOCATION }__${ this.props.id }`, this.handleFocusLocation, this );
        }


        /****************************************************/

        /**
         * @desc This method is called automatically when Google API JavaScript file is loaded in the browser.
         * @param {*} google - Google Map API object
         */
        renderGoogleMap( props ) {
            log.debug( 'GoogleMap.renderGoogleMap()' );

            // if map dependency is `loading` or `locations` are empty, avoid map render
            if( isEmpty( props.locations ) ) {
                return;
            }

            // get google api
            const { google } = props.asyncDependencies;

            // marker with label class
            const MarkerWithLabel = markerWithLabelLoader( google.maps );

            // create map bounds (to contain all markers)
            const bounds = new google.maps.LatLngBounds();

            // create google map instance if doesn't already exists
            if( isEmpty( this.data.map ) ) {
                const map = new google.maps.Map( this.mapElemRef.current, {
                    disableDefaultUI: true,
                    zoomControl: true,
                    maxZoom: 19 // max zoom attribute ensures that the map is displayed with wider view
                } );
    
                // save map in component data
                this.data.map = map;
            }

            // remove all exisiting markers from the map
            forEach( this.data.markers, ( marker ) => {
                marker.setMap( null );
            } );

            // reset stored markers
            this.data.markers = {};

            // create a info window (popover for marker)
            const infoWindow = new google.maps.InfoWindow();

            // for each location, add marker to the map
            forEach( props.locations, ( location ) => {

                // create geometry position (point)
                const position = new google.maps.LatLng( location.lat, location.long );

                // extend bounds with position
                bounds.extend( position );
                
                // create marker object with label
                const marker = new MarkerWithLabel( {
                    position: position,
                    map: this.data.map,
                    animation: google.maps.Animation.DROP,
                    icon: {
                        url: 'data:image/png;base64,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' },
                        
                    // label
                    labelContent: location.name,
                    labelClass: 'ui-google-map__map__marker__label'
                } );

                // save marker in component data using location id
                this.data.markers[ location.id ] = marker;

                // add `click` event listener to `marker` object and open info window
                google.maps.event.addListener( marker, 'click', () => {

                    // get static HTML marker from `infoWindow` data
                    infoWindow.setContent( ReactDomServer.renderToStaticMarkup( <props.infoWindowView { ...location.infoWindow.props } /> ) );

                    // open info window
                    infoWindow.open( this.data.map, marker );
                } );

            } );

            // auto center and auto zoom map using bounds
            this.data.map.fitBounds( bounds ); // auto-zoom
            this.data.map.panToBounds( bounds ); // auto-center
        }

        /**
         * @desc Focus a location (marker) and open info window
         */
        handleFocusLocation( { id } ) {
            log.debug( 'GoogleMap.handleFocusLocation()' );

            // get marker by location id
            const marker = this.data.markers[ id ];

            // get google api
            const { google } = this.props.asyncDependencies;

            if( ! isEmpty( marker ) && ! isEmpty( this.data.map ) ) {

                // trigger click event on map marker
                google.maps.event.trigger( marker, 'click' );

                // pan map to the focused location
                this.data.map.panTo( marker.getPosition() );
            }
        }

        /****************************************************/

        /**
         * @desc When component is mounted, load google map into map element
         */
        componentDidMount() {
            log.debug( 'GoogleMap.componentDidMount()' );

            // when component mounts, render google map
            this.renderGoogleMap( this.props );
        }

        /**
         * @desc When location changes, update state and re-render the map
         */
        UNSAFE_componentWillReceiveProps( newProps ) {
            log.debug( 'GoogleMap.componentWillReceiveProps()' );

            // if `locations` changes, re-render map
            if( ! isEqual( newProps.locations, this.props.locations ) ) {
                this.renderGoogleMap( newProps ); // re-render map if map dependencies are loaded
            }
        }

        /**
         * @desc When component is unmounted, remove event listeners
         */
        componentWillUnmount() {
            log.debug( 'GoogleMap.componentWillUnmount()' );

            this.__unbind();
        }

        /**
         * @desc Render map
         */
        render() {
            log.debug( 'GoogleMap.render()' );

            return (
                <Component
                    ref={ this.mapElemRef }
                    className={ this.props.className }
                    imageUrl = { IMAGE_GOOGLE_MAP }
                />
            );
        }
    }

    // set display name
    GoogleMap.displayName = 'GoogleMap';
    
    // default props
    GoogleMap.defaultProps = {};

    // prop types
    GoogleMap.propTypes = {
        id: PropTypes.string.isRequired,
        locations: PropTypes.arrayOf( PropTypes.shape( {
            id: PropTypes.any,
            lat: PropTypes.number,
            lng: PropTypes.number,
            label: PropTypes.string,
            infoWindow: PropTypes.shape( {
                props: PropTypes.object, // props for infoWindowView
            } )
        } ) ).isRequired,
        infoWindowView: PropTypes.any, // React view to display popover for marker
    };

    // return component
    return GoogleMap;
};

// export HOC
export { googleMap };

// export react component with default view
export const GoogleMap = googleMap();

// load with external google API dependency
//export const GoogleMap = withAsyncDependencies( _GoogleMap );

